import { Box, Flex, Heading, Text, Button, Link } from "@chakra-ui/react";
import { ProcessViewModel } from "../../../models/Process";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { crm } from "../../../theme";
import ProcessUrgencyBadge from "../../../components/ProcessUrgencyBadge";

const ProcessCard = ({
  p,
  isDashboard = false,
}: {
  p: ProcessViewModel;
  isDashboard?: boolean;
}) => {
  const navigate = useNavigate();
  const auth = useAuthUser<any>();

  return (
    <Box
      w="100%"
      borderRadius="12px"
      backgroundColor={p.isUrgent ? "rgba(220,38,38,0.06)" : "white"}
      p="24px"
      border="1px solid"
      borderColor={p.isUrgent ? crm.danger : crm.border}
      borderLeftWidth={p.isUrgent ? "5px" : "1px"}
      boxShadow={crm.shadowSm}
      transition="all .15s"
      _hover={{
        boxShadow: crm.shadowMd,
        transform: "translateY(-1px)",
        borderColor: crm.brand,
      }}
    >
      <Flex
        justifyContent="space-between"
        flexDir={{ base: "column", md: "row" }}
      >
        <Flex
          flexDir={{ base: "column", md: "row" }}
          alignItems="center"
          gap={{ base: "12px", md: "24px" }}
        >
          <Flex
            justifyContent="space-between"
            gap={{ base: "12px", md: "24px" }}
          >
            <Flex alignItems="center" gap="6px">
              <Heading size="md">Processo {p.cvfRef}</Heading>
              {p.isUrgent && <ProcessUrgencyBadge compact />}
            </Flex>

            {auth?.position !== 1 && (
              <Flex alignItems="center">
                <Text fontWeight="semibold" maxW="300px" size="xl">
                  {p.exporter}
                </Text>
              </Flex>
            )}
          </Flex>
          {p.importMode && (
            <Flex
              alignItems="center"
              gap="15px"
              backgroundColor={crm.brandSoft}
              h="40px"
              color={crm.brand}
              p="8px"
              borderRadius="8px"
            >
              <Heading size="sm" fontWeight="normal" textAlign="center">
                {p.importMode}
              </Heading>
            </Flex>
          )}
          {(p.channel?.length ?? 0) > 1 && (
            <Flex
              backgroundColor={
                p.channel?.toLowerCase() === "verde"
                  ? "#27AE60"
                  : p.channel?.toLowerCase() === "amarelo"
                    ? "#F1C40F"
                    : p.channel?.toLowerCase() === "vermelho"
                      ? "#E74C3C"
                      : "white"
              }
              h="40px"
              p="8px"
              justifyContent={{ base: "center", md: "flex-start" }}
              borderRadius="4px"
              alignItems="center"
              gap="16px"
            >
              <Text size="md" color="white" textAlign="center">
                CANAL {p.channel?.toUpperCase()}
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex justifyContent="center" alignItems="center" gap="16px">
          <Text
            fontWeight="semibold"
            w="140px"
            ml="16px"
            size="md"
            textAlign="center"
          >
            {p.status}
          </Text>
          <Link href={`/process?id=${p.id}${isDashboard ? "&isDashboard=true" : ""}`} style={{ textDecoration: 'none' }}>
            <Button
              display={{ base: "none", md: "block" }}
              variant="outline"
              borderRadius="10px"
              color={crm.brand}
              borderColor={crm.brand}
              _hover={{ bg: crm.brandSoft }}
            >
              Visualizar detalhes
            </Button>
          </Link>
        </Flex>
      </Flex>
      <Flex
        mt="32px"
        gap="32px"
        flexDir={{ base: "column", md: "row" }}
        overflowX="auto"
        pb="12px"
      >
        <Box
          w={{ base: "400px", md: "142px" }}
          minW={{ base: "400px", md: "142px" }}
          flexShrink={0}
        >
          <Heading size="xs">Ref. do cliente</Heading>
          <Text size="md">{p.clientRef}</Text>
        </Box>
        <Box
          w={{ base: "400px", md: "72px" }}
          minW={{ base: "400px", md: "72px" }}
        >
          <Heading size="xs">Incoterm</Heading>
          <Text wordBreak="break-word" size="md">
            {p.incoterm}
          </Text>
        </Box>
        {p.freight && (
          <Box w="240px">
            <Heading size="xs">Frete</Heading>
            <Text size="md">{p.freight}</Text>
          </Box>
        )}

        <Flex flexShrink={0}>
          <Box
            w={{ base: "200px", md: "100px" }}
            minW={{ base: "200px", md: "100px" }}
          >
            <Heading size="xs">NCM</Heading>
            <Text wordBreak="break-word" size="md">
              {p.ncm}
            </Text>
          </Box>

          <Box maxW={{ base: "200px", md: "260px" }} flexShrink={0}>
            <Heading size="xs">Produto</Heading>
            <Text wordBreak="break-word" size="md">
              {p.product}
            </Text>
          </Box>
          <Box
            w={{ base: "200px", md: "92px" }}
            minW={{ base: "200px", md: "92px" }}
            ml="32px"
          >
            <Heading size="xs">Origem</Heading>
            <Text size="md">{p.countryOfOrigin}</Text>
          </Box>
          <Box
            w={{ base: "200px", md: "122px" }}
            minW={{ base: "200px", md: "122px" }}
            ml="32px"
          >
            <Heading size="xs">Destino</Heading>
            <Text size="md">{p.dischargePort}</Text>
          </Box>
        </Flex>

        {auth?.position !== 1 && (
          <Box
            w={{ base: "200px", md: "110px" }}
            minW={{ base: "200px", md: "110px" }}
            flexShrink={0}
          >
            <Heading size="xs">Valor</Heading>
            <Text wordBreak="break-word" size="md">
              {p.totalInvoice?.replace("$", "USD ")}
            </Text>
          </Box>
        )}

        {(p.di?.length ?? 0) > 1 && (
          <Box
            w={{ base: "400px", md: "100px" }}
            minW={{ base: "400px", md: "100px" }}
            flexShrink={0}
          >
            <Heading size="xs">DI</Heading>
            <Text size="md">{p.di}</Text>
          </Box>
        )}

        <Flex flexShrink={0}>
          {p.shippingDate && (
            <Box
              w={{ base: "200px", md: "92px" }}
              minW={{ base: "200px", md: "92px" }}
            >
              <Heading size="xs">ETD</Heading>
              <Text wordBreak="break-word" size="md">
                {DateTime.fromISO(p.shippingDate).toFormat("dd/LL/yyyy")}
              </Text>
            </Box>
          )}

          {p.arrivalDate && (
            <Box
              w={{ base: "200px", md: "92px" }}
              minW={{ base: "200px", md: "92px" }}
              ml={p.shippingDate ? "32px" : "0"}
            >
              <Heading size="xs">ETA</Heading>
              <Text wordBreak="break-word" size="md">
                {DateTime.fromISO(p.arrivalDate).toFormat("dd/LL/yyyy")}
              </Text>
            </Box>
          )}
        </Flex>
        <Flex flexShrink={0}>
          <Box
            w={{ base: "200px", md: "130px" }}
            minW={{ base: "200px", md: "130px" }}
          >
            <Heading size="xs">Nacionalização</Heading>
            {p.nfIssued != null && (
              <Text size="md">
                {
                  DateTime.fromISO(p.nfIssued).diff(
                    DateTime.fromISO(p.arrivalDate ?? ""),
                    "days",
                  ).days
                }{" "}
                dias
              </Text>
            )}
          </Box>
          <Box ml="32px">
            <Heading size="xs" w="100px">
              Transit Time
            </Heading>
            {p.arrivalDate !== null && (
              <Text size="md">
                {
                  DateTime.fromISO(p.arrivalDate ?? "").diff(
                    DateTime.fromISO(p.shippingDate ?? ""),
                    "days",
                  ).days
                }{" "}
                dias
              </Text>
            )}
          </Box>
        </Flex>
        <Link href={`/process?id=${p.id}`} style={{ textDecoration: 'none' }}>
          <Button
            display={{ base: "block", md: "none" }}
            variant="outline"
            borderRadius="10px"
            color={crm.brand}
            borderColor={crm.brand}
            _hover={{ bg: crm.brandSoft }}
            flexShrink={0}
          >
            Visualizar detalhes
          </Button>
        </Link>
      </Flex>
    </Box>
  );
};

export default ProcessCard;
